import { LoadState } from "../../constants/enums";
import { ISharedMailboxState, IMailboxUnreadCounts } from "./sharedMailbox.types";

export interface IOutlookState {
  connection: {
    connected: boolean;
    email: string;
    expiresAt: string;
    isExpired: boolean;
    loading: LoadState;
  };
  selectedMessageIds: string[];
  emails: {
    list: IOutlookInboxEmail[];
    hasMore: boolean;
    loading: LoadState;
  };
  folders: {
    list: IOutlookFolder[];
    loading: LoadState;
  };
  selectedFolderId: string | null;
  emailsMeta: {
    searchQuery: string;
    searchNextLink: string | null;
    labelFilter: string;
    readFilter: "all" | "read" | "unread";
    flagFilter: "all" | "notFlagged" | "flagged" | "complete";
    advancedSearch: IOutlookAdvancedSearch;
    pagination: {
      page: number;
      pageSize: number;
    };
  };
  messageById: {
    messageData: IOutlookMessageById | null;
    loading: LoadState;
  };
  notifications: {
    list: IOutlookNotification[];
    unreadCount: number;
    loading: LoadState;
    fetchingMore: boolean;
  };
  labels: {
    list: IOutlookLabel[];
    loading: LoadState;
  };
  calenderEvents: {
    list: IOutlookCalenderEvent[];
    loading: LoadState;
    eventDataById: IOutlookCalenderEvent;
    eventDataByIdLoading: LoadState;
  };
  childFolders: Record<string, { list: IOutlookFolder[]; loading: LoadState }>;
  activeMailbox: string | null;
  sharedMailboxes: ISharedMailboxState;
  mailboxUnreadCounts: IMailboxUnreadCounts;
}

export interface IOutlookAdvancedSearch {
  from: string;
  to: string;
  subject: string;
  hasAttachments: "all" | "yes" | "no";
  importance: "all" | "low" | "normal" | "high";
  dateFrom: string;
  dateTo: string;
}

export const initialAdvancedSearch: IOutlookAdvancedSearch = {
  from: "",
  to: "",
  subject: "",
  hasAttachments: "all",
  importance: "all",
  dateFrom: "",
  dateTo: "",
};

export interface IOutlookLabel {
  id: string;
  displayName: string;
  color: string;
}
export interface IOutlookFolder {
  id: string;
  displayName: string;
  childFolderCount: number;
  unreadItemCount: number;
  totalItemCount: number;
}
export interface IOutlookNotification {
  outlook_notification_code: number;
  user_fact_id: number;
  message_id: string;
  subject: string;
  from_email: string;
  from_name: string;
  to_recipients: string;
  cc_recipients: string | null;
  body_preview: string;
  received_datetime: string;
  folder: string;
  conversation_id: string;
  importance: "low" | "normal" | "high";
  read_status: "READ" | "UNREAD";
  create_ts: string;
  mailboxEmail?: string | null;
}

export interface IOutlookMessageById {
  "@odata.context": string;
  "@odata.etag": string;
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  changeKey: string;
  categories: string[];
  receivedDateTime: string;
  sentDateTime: string;
  hasAttachments: boolean;
  internetMessageId: string;
  subject: string;
  bodyPreview: string;
  importance: "low" | "normal" | "high";
  parentFolderId: string;
  conversationId: string;
  conversationIndex: string;
  isDeliveryReceiptRequested: boolean | null;
  isReadReceiptRequested: boolean;
  isRead: boolean;
  isDraft: boolean;
  webLink: string;
  inferenceClassification: "focused" | "other";

  body: {
    contentType: "text" | "html";
    content: string;
  };

  sender: GraphEmailAddress;
  from: GraphEmailAddress;
  toRecipients: GraphEmailAddress[];
  ccRecipients: GraphEmailAddress[];
  bccRecipients: GraphEmailAddress[];
  replyTo: GraphEmailAddress[];

  flag: {
    flagStatus: OUTLOOK_MESSAGE_FLAGS;
  };

  attachments: GraphAttachment[];
}

export type OUTLOOK_MESSAGE_FLAGS = "notFlagged" | "flagged" | "complete";

export interface GraphEmailAddress {
  emailAddress: {
    name: string;
    address: string;
  };
}

export interface GraphAttachment {
  "@odata.type"?: string;
  "@odata.mediaContentType"?: string;
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline?: boolean;
  contentId?: string;
  contentLocation?: string | null;
  lastModifiedDateTime?: string;
  contentBytes?: string;
}
export interface IOutlookInboxEmail {
  "@odata.etag": string;
  id: string;
  receivedDateTime: string;
  hasAttachments: boolean;
  subject: string;
  bodyPreview: string;
  importance: "low" | "normal" | "high";
  isRead: boolean;
  from: Recipient;
  toRecipients: Recipient[];
  ccRecipients: Recipient[];
  bccRecipients: Recipient[];
  categories: string[];
  flag: {
    flagStatus: OUTLOOK_MESSAGE_FLAGS;
  };
  mailboxEmail?: string;
}

export interface Recipient {
  emailAddress: EmailAddress;
}

export interface EmailAddress {
  name: string;
  address: string;
}

export interface IOutlookEmailAttachmentPayload {
  name: string;
  contentType: string;
  contentBytes: string;
}

export interface IOutlookEmailComposePayload {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  attachments: IOutlookEmailAttachmentPayload[];
  module_name?: string;
  module_code?: string | number | null;
  objectVariables?: Record<string, any>;
  templateId?: string | null;
  bind_key?: string | null;
  bind_value?: string | number | null;
}

export interface IOutlookCalenderEvent {
  "@odata.etag": string;
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  changeKey: string;
  categories: string[];
  transactionId: string;

  originalStartTimeZone: string;
  originalEndTimeZone: string;

  iCalUId: string;
  uid: string;

  reminderMinutesBeforeStart: number;
  isReminderOn: boolean;
  hasAttachments: boolean;

  subject: string;
  bodyPreview: string;

  importance: "low" | "normal" | "high";
  sensitivity: "normal" | "personal" | "private" | "confidential";

  isAllDay: boolean;
  isCancelled: boolean;
  isOrganizer: boolean;
  responseRequested: boolean;

  seriesMasterId: string | null;
  showAs: "free" | "tentative" | "busy" | "oof" | "workingElsewhere";
  type: "singleInstance" | "occurrence" | "exception" | "seriesMaster";

  webLink: string;

  onlineMeetingUrl: string | null;
  isOnlineMeeting: boolean;
  onlineMeetingProvider: string;

  allowNewTimeProposals: boolean;
  occurrenceId: string | null;
  isDraft: boolean;
  hideAttendees: boolean;

  responseStatus: {
    response: string;
    time: string;
  };

  body: {
    contentType: "text" | "html";
    content: string;
  };

  start: DateTimeTimeZone;
  end: DateTimeTimeZone;

  location: Location;
  locations: Location[];

  recurrence: any | null; // can be expanded if needed

  attendees: Attendee[];

  organizer: {
    emailAddress: EmailAddress;
  };

  onlineMeeting: any | null;

  "calendar@odata.associationLink": string;
  "calendar@odata.navigationLink": string;
}

export interface DateTimeTimeZone {
  dateTime: string;
  timeZone: string;
}

export interface Location {
  displayName: string;
  locationType?: string;
  uniqueIdType?: string;
  address?: PhysicalAddress;
  coordinates?: Coordinates;
}

export interface PhysicalAddress {
  street?: string;
  city?: string;
  state?: string;
  countryOrRegion?: string;
  postalCode?: string;
}

export interface Coordinates {
  latitude?: number;
  longitude?: number;
}

export interface Attendee {
  type: "required" | "optional" | "resource";
  status: {
    response: string;
    time: string;
  };
  emailAddress: EmailAddress;
}

export interface EmailAddress {
  name: string;
  address: string;
}

export interface IOutlookEventPayload {
  subject: string;
  body: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  attendees: Attendee[];
  location: Location;
  isOnlineMeeting: boolean;
  isAllDay: boolean;
}

export const initialOutlookEventPayload: IOutlookEventPayload = {
  subject: "",
  body: "",
  start: {
    dateTime: "",   
    timeZone: "",
  },
  end: {
    dateTime: "",
    timeZone: ""
  },
  attendees: [],
  location: {
    displayName: "",
    locationType: "",
    uniqueIdType: "", 
    address: {
      street: "",
      city: "",
      state: "",
      countryOrRegion: "",
      postalCode: "",
    },
    coordinates: {
      latitude: 0,
      longitude: 0,
    },  
  },
  isOnlineMeeting: false,
  isAllDay: false,
};

export const initialOutlookState: IOutlookState = {
  activeMailbox: null,
  sharedMailboxes: {
    list: [],
    loading: LoadState.NotLoaded,
    registerLoading: LoadState.NotLoaded,
    verifyLoading: LoadState.NotLoaded,
    unregisterLoading: LoadState.NotLoaded,
    verifyResult: null,
  },
  mailboxUnreadCounts: {},
  childFolders: {},
  connection: {
    connected: false,
    email: "",
    expiresAt: "",
    isExpired: false,
    loading: LoadState.NotLoaded,
  },
  emails: {
    list: [],
    hasMore: true,
    loading: LoadState.NotLoaded,
  },
  folders: {
    list: [],
    loading: LoadState.NotLoaded,
  },
  selectedMessageIds: [],
  selectedFolderId: null,
  emailsMeta: {
    searchQuery: "",
    searchNextLink: null,
    labelFilter: "",
    readFilter: "all",
    flagFilter: "all",
    advancedSearch: initialAdvancedSearch,
    pagination: {
      page: 0,
      pageSize: 20,
    },
  },
  messageById: {
    messageData: null,
    loading: LoadState.NotLoaded,
  },
  notifications: {
    list: [],
    unreadCount: 0,
    loading: LoadState.NotLoaded,
    fetchingMore: true,
  },
  labels: {
    list: [],
    loading: LoadState.NotLoaded,
  },
  calenderEvents: {
    list: [],
    loading: LoadState.NotLoaded,
    eventDataById: {
      "@odata.etag": "",
      id: "",
      createdDateTime: "",
      lastModifiedDateTime: "",
      changeKey: "",          
      categories: [],
      transactionId: "",
      originalStartTimeZone: "",
      originalEndTimeZone: "",
      iCalUId: "",
      uid: "",
      reminderMinutesBeforeStart: 0,
      isReminderOn: false,
      hasAttachments: false,
      subject: "",
      bodyPreview: "",    
      importance: "low",  
      sensitivity: "normal",
      isAllDay: false,
      isCancelled: false,
      isOrganizer: false,
      responseRequested: false,
      seriesMasterId: null,
      showAs: "free",
      type: "singleInstance",
      webLink: "",
      onlineMeetingUrl: null,
      isOnlineMeeting: false,
      onlineMeetingProvider: "",
      allowNewTimeProposals: false,
      occurrenceId: null,
      isDraft: false,
      hideAttendees: false,
      responseStatus: {
        response: "",
        time: "",
      },
      body: {
        contentType: "text",
        content: "",
      },
      start: {
        dateTime: "",
        timeZone: "",
      },
      end: {
        dateTime: "",
        timeZone: "",
      },
      location: {
        displayName: "",
        locationType: "",
        uniqueIdType: "",
        address: {
          street: "",
          city: "",
          state: "",    
          countryOrRegion: "",
          postalCode: "",
        },
        coordinates: {
          latitude: 0,      
            longitude: 0,   
        },    
      },
      locations: [],
      attendees: [],
      organizer: {
        emailAddress: {
          name: "",
          address: "",
        },
      },
      onlineMeeting: null,      
      "calendar@odata.associationLink": "",
      "calendar@odata.navigationLink": "",
      recurrence: null,
    },
    eventDataByIdLoading: LoadState.NotLoaded,
  },
};
